import React, { useRef, useState, useEffect } from 'react';
import './VideoPlayer.css';

const formatTime = (t) => {
  if (!isFinite(t) || isNaN(t)) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const VideoPlayer = ({ src, loop = true, onVideoClick }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onLoadedMetadata = () => setDuration(video.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  const togglePlay = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    video.paused ? video.play() : video.pause();
  };

  const skip = (e, seconds) => {
    e.stopPropagation();
    const video = videoRef.current;
    video.currentTime = Math.max(0, Math.min(video.currentTime + seconds, duration));
  };

  const enterFullscreen = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video.requestFullscreen) video.requestFullscreen();
    else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
    else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
  };

  const onSeek = (e) => {
    e.stopPropagation();
    const val = Number(e.target.value);
    videoRef.current.currentTime = val;
    setCurrentTime(val);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="video-player">
      <div
        className={`video-player__screen${onVideoClick ? ' section-image--clickable' : ''}`}
        onClick={onVideoClick}
      >
        <video
          ref={videoRef}
          src={src}
          autoPlay
          loop={loop}
          muted
          playsInline
          className="video-player__video"
        />
      </div>

      <div className="video-player__controls" onClick={e => e.stopPropagation()}>
        <button className="video-player__btn" onClick={(e) => skip(e, -10)} aria-label="Rewind 10 seconds">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 4v6h6"/>
            <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
          </svg>
          <span>10</span>
        </button>

        <button className="video-player__btn video-player__btn--play" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1"/>
              <rect x="14" y="4" width="4" height="16" rx="1"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
          )}
        </button>

        <button className="video-player__btn video-player__btn--forward" onClick={(e) => skip(e, 10)} aria-label="Fast forward 10 seconds">
          <span>10</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6"/>
            <path d="M20.49 15a9 9 0 1 1-.49-3.51"/>
          </svg>
        </button>

        <span className="video-player__time">{formatTime(currentTime)}</span>

        <div className="video-player__seekbar-wrap">
          <div className="video-player__seekbar-track">
            <div className="video-player__seekbar-fill" style={{ width: `${progress}%` }} />
          </div>
          <input
            type="range"
            className="video-player__seekbar"
            min={0}
            max={duration || 100}
            step={0.01}
            value={currentTime}
            onChange={onSeek}
            onMouseDown={e => e.stopPropagation()}
          />
        </div>

        <span className="video-player__time">{formatTime(duration)}</span>

        <button className="video-player__btn" onClick={enterFullscreen} aria-label="Full screen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3"/>
            <path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
            <path d="M3 16v3a2 2 0 0 0 2 2h3"/>
            <path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default VideoPlayer;
